/**
 * SA-1329: the bulk run. Decides a selection's rows one after another through
 * today's routes; the inbox store holds the run, the page refetches as rows settle.
 * Every step checks the store still holds this run (an identity change resets it).
 */
import { ApiError } from '@/services/apiClient'
import { triageService, triageErrorMessage } from '@/services/triage'
import { useInboxStore } from '@/stores/inboxStore'
import type { TriageRun, TriageRunItem } from '@/types/inbox'

export interface RunRequest {
  action: TriageRun['action']
  origin: TriageRun['origin']
  kind?: string
  items: Omit<TriageRunItem, 'state' | 'sentence'>[]
}

const holds = (id: string) => useInboxStore.getState().triageRun?.id === id

function send(action: TriageRun['action'], kind: string | undefined, item: RunRequest['items'][number]) {
  const { conversationId, ledgerId, mailboxId, outcome } = item
  switch (action) {
    case 'approve':
      return triageService.decide(conversationId, { ledger_id: ledgerId, ...(outcome !== undefined ? { outcome } : {}) })
    case 'change':
      return triageService.decide(conversationId, { ledger_id: ledgerId, kind })
    case 'dismiss':
      return triageService.decide(conversationId, { ledger_id: ledgerId, kind: 'internal' })
    case 'rerun':
      if (mailboxId === null) throw new Error('no mailbox')
      return triageService.rerun(mailboxId, ledgerId)
  }
}

async function runItems(id: string, req: RunRequest) {
  for (const item of req.items) {
    if (!holds(id)) return
    const store = useInboxStore.getState()
    store.patchTriageRunItem(id, item.key, { state: 'deciding' })
    let patch: Partial<TriageRunItem>
    try {
      await send(req.action, req.kind, item)
      patch = { state: 'done' }
    } catch (err) {
      const conflict = err instanceof ApiError && err.status === 409 &&
        (err.detail as { code?: unknown } | null)?.code === 'triage_conflict'
      patch = conflict ? { state: 'conflict' } : { state: 'refused', sentence: triageErrorMessage(err) }
    }
    if (!holds(id)) return
    useInboxStore.getState().patchTriageRunItem(id, item.key, patch)
  }
  useInboxStore.getState().finishTriageRun(id)
}

/** Starts a run; null (nothing started) while one is running or when there is nothing to decide. Returns the run id at once. */
export function startTriageRun(req: RunRequest): string | null {
  if (req.items.length === 0) return null
  if (useInboxStore.getState().triageRun?.status === 'running') return null
  const id = crypto.randomUUID()
  useInboxStore.getState().beginTriageRun({
    id,
    action: req.action,
    origin: req.origin,
    kind: req.kind ?? null,
    settled: 0,
    status: 'running',
    items: req.items.map((i) => ({ ...i, state: 'queued', sentence: null })),
  })
  void runItems(id, req)
  return id
}
